import { useCallback, useEffect, useRef, useState } from "react";

import {
  cancelUpload,
  directUpload,
  getUploadStatus,
  resumeDirectUpload,
} from "@/queries/uploads";

export type DocumentUploadStatus = "uploading" | "completed" | "error";

export interface DocumentUpload {
  /** Identidad estable en el cliente: el índice no sirve porque la lista muta. */
  key: string;
  file: File;
  status: DocumentUploadStatus;
  /** 0–100. Llega a 100 sólo cuando el backend confirmó el upload. */
  progress: number;
  /**
   * `upload_id` del registro en el backend. Se conoce apenas se crea —antes de
   * mover bytes— así que también está presente en `uploading` y en `error`, que
   * es lo que permite reintentar o cancelar. Sólo es adjuntable en `completed`.
   */
  uploadId?: string;
  /**
   * Presente sólo cuando este registro usó una sesión resumible (>100 MB).
   * Ausente en subidas por `signed_url` (≤100 MB) o mientras el primer intento
   * sigue en vuelo. Su ausencia es justo lo que hace que `retryDocument`
   * reinicie desde cero en vez de retomar la sesión.
   */
  resumableSessionUri?: string;
  error?: string;
}

interface UseDocumentUploadsOptions {
  /** Se envían a `request-url` para que el backend sepa de dónde viene. */
  context?: string;
  contextId?: string;
  /**
   * Cuando se define, los uploads ya confirmados sobreviven a una recarga de
   * página: se guardan en `sessionStorage` bajo esta llave y al volver a montar
   * se revalidan contra el backend.
   */
  persistKey?: string;
}

/** Lo mínimo para volver a dibujar y adjuntar un upload tras una recarga. */
interface PersistedUpload {
  uploadId: string;
  name: string;
  size: number;
  type: string;
}

const readPersisted = (key: string): PersistedUpload[] => {
  try {
    const raw = globalThis.sessionStorage?.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PersistedUpload[]) : [];
  } catch {
    // Modo privado o almacenamiento bloqueado: la recuperación es best-effort.
    return [];
  }
};

const writePersisted = (key: string, entries: PersistedUpload[]): void => {
  try {
    if (entries.length === 0) globalThis.sessionStorage?.removeItem(key);
    else globalThis.sessionStorage?.setItem(key, JSON.stringify(entries));
  } catch {
    // Igual que arriba: no vale la pena romper el formulario por esto.
  }
};

/**
 * Reconstruye un `File` a partir de lo guardado. Los bytes ya viven en GCS y no
 * sobreviven a la recarga, así que este objeto sólo existe para que el resto del
 * formulario —la lista y el resolver de zod— siga viendo un `File` con el nombre
 * y el peso correctos. Nadie lee su contenido: adjuntar usa el `upload_id`.
 */
const describeRecoveredFile = (entry: PersistedUpload): File => {
  const file = new File([], entry.name, { type: entry.type });
  Object.defineProperty(file, "size", { value: entry.size });
  return file;
};

/**
 * Sube los archivos en cuanto el usuario los selecciona, no al enviar el
 * formulario. Los bytes viajan directo a GCS mientras el usuario sigue
 * llenando el resto del formulario; al enviar sólo queda asociar los
 * `upload_id` ya confirmados con la entidad recién creada.
 *
 * La validación (tamaño y tipo) es responsabilidad del llamador: aquí sólo
 * llegan archivos que ya pasaron las reglas del formulario.
 */
export const useDocumentUploads = ({
  context,
  contextId,
  persistKey,
}: UseDocumentUploadsOptions = {}) => {
  const [documents, setDocuments] = useState<DocumentUpload[]>([]);
  const [isRecovering, setIsRecovering] = useState(Boolean(persistKey));
  const nextKey = useRef(0);
  /** Un `AbortController` por archivo, para poder cortar lo que siga en vuelo. */
  const controllers = useRef(new Map<string, AbortController>());

  const patch = useCallback((key: string, changes: Partial<DocumentUpload>) => {
    setDocuments((prev) =>
      prev.map((doc) => (doc.key === key ? { ...doc, ...changes } : doc)),
    );
  }, []);

  /**
   * Lanza la subida de un archivo ya registrado en la lista.
   *
   * No cancela el registro si falla: el archivo queda en `error` conservando su
   * `upload_id`, que es lo que permite ofrecer "Reintentar" y, al cerrar el
   * modal, borrar exactamente lo que quedó a medias.
   */
  const startUpload = useCallback(
    (key: string, file: File) => {
      const controller = new AbortController();
      controllers.current.set(key, controller);

      directUpload(file, {
        context,
        context_id: contextId,
        cancelOnFailure: false,
        signal: controller.signal,
        onUploadCreated: (uploadId, info) =>
          patch(key, {
            uploadId,
            resumableSessionUri: info?.uploadSessionUri ?? undefined,
          }),
        onProgress: (pct) => patch(key, { progress: pct }),
      })
        .then((result) =>
          patch(key, {
            status: "completed",
            progress: 100,
            uploadId: result.id,
          }),
        )
        .catch((err: unknown) => {
          // Abortar es una decisión nuestra (cerrar el modal, quitar el
          // archivo), no un fallo que el usuario deba ver.
          if (err instanceof DOMException && err.name === "AbortError") return;
          patch(key, {
            status: "error",
            error:
              err instanceof Error
                ? err.message
                : "No se pudo subir el archivo",
          });
        })
        .finally(() => controllers.current.delete(key));
    },
    [context, contextId, patch],
  );

  /**
   * Retoma un archivo que quedó en `error` pero ya tenía una sesión resumible
   * en curso (>100 MB): en vez de descartarla y volver a mandar todos los
   * bytes, `resumeDirectUpload` le pregunta a GCS cuánto tiene y sólo manda lo
   * que falta. Si la sesión ya expiró, `resumeDirectUpload` mismo cae de
   * vuelta a un `directUpload` desde cero y reporta el `id`/sesión nuevos vía
   * `onUploadCreated`.
   */
  const resumeUploadAttempt = useCallback(
    (key: string, file: File, uploadId: string, sessionUri: string) => {
      const controller = new AbortController();
      controllers.current.set(key, controller);
      patch(key, { status: "uploading", error: undefined });

      resumeDirectUpload(file, {
        id: uploadId,
        sessionUri,
        context,
        context_id: contextId,
        cancelOnFailure: false,
        signal: controller.signal,
        onUploadCreated: (newId, info) =>
          patch(key, {
            uploadId: newId,
            resumableSessionUri: info?.uploadSessionUri ?? undefined,
          }),
        onProgress: (pct) => patch(key, { progress: pct }),
      })
        .then((result) =>
          patch(key, {
            status: "completed",
            progress: 100,
            uploadId: result.id,
          }),
        )
        .catch((err: unknown) => {
          if (err instanceof DOMException && err.name === "AbortError") return;
          patch(key, {
            status: "error",
            error:
              err instanceof Error
                ? err.message
                : "No se pudo subir el archivo",
          });
        })
        .finally(() => controllers.current.delete(key));
    },
    [context, contextId, patch],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const entries: DocumentUpload[] = files.map((file) => ({
        key: `doc-${nextKey.current++}`,
        file,
        status: "uploading",
        progress: 0,
      }));

      setDocuments((prev) => [...prev, ...entries]);

      // En paralelo a propósito: cada archivo reporta su propio progreso y el
      // usuario no queda bloqueado esperando al anterior.
      for (const entry of entries) {
        startUpload(entry.key, entry.file);
      }
    },
    [startUpload],
  );

  // Espejo de `documents` para poder reconciliar sin re-suscribir el callback.
  const documentsRef = useRef<DocumentUpload[]>([]);
  useEffect(() => {
    documentsRef.current = documents;
  }, [documents]);

  /**
   * Red de seguridad si el componente se desmonta con algo todavía en vuelo:
   * el modal que lo usa se cierra sin pasar por `discardAll` (el padre lo
   * desmonta directamente, por ejemplo `{modalState === "x" && <Modal />}`) o
   * el usuario navega a otra página. Sin esto el PUT a GCS seguía viéndose
   * "pending" en la red aunque la UI ya no estuviera.
   *
   * Sólo toca lo que sigue en `controllers`: un upload ya completado o
   * fallido se quita de ahí en su `.finally` (ver `startUpload`), así que
   * esto nunca cancela algo que ya terminó de subir o que ya se adjuntó.
   */
  useEffect(() => {
    return () => {
      for (const [key, controller] of controllers.current) {
        controller.abort();
        const doc = documentsRef.current.find((d) => d.key === key);
        if (doc?.uploadId) {
          cancelUpload(doc.uploadId).catch(() => {
            /* best-effort: si ya expiró o no se puede cancelar, no hay nada que hacer */
          });
        }
      }
    };
  }, []);

  /**
   * Corta lo que siga en vuelo y pide al backend borrar el registro.
   *
   * Sólo se pide el `DELETE` para lo que quedó a medias (`uploading` o
   * `error`): un upload `completed` ya no debe tocarse desde el cliente —el
   * backend rechaza cancelar uno ya `COMPLETED`, así que llamarlo ahí sólo
   * generaba una petición sin efecto. Los confirmados que nunca se asociaron
   * los recoge una limpieza nocturna del backend, así que no hace falta
   * perseguirlos desde el cliente.
   */
  const discardUpload = useCallback((doc: DocumentUpload) => {
    controllers.current.get(doc.key)?.abort();
    controllers.current.delete(doc.key);
    if (doc.uploadId && doc.status !== "completed") {
      cancelUpload(doc.uploadId).catch(() => {
        /* si ya expiró o no se puede cancelar, no hay nada que hacer */
      });
    }
  }, []);

  /**
   * Reconcilia contra una lista externa de archivos (por ejemplo la que
   * mantiene `useFileDropZone`, cuyo callback entrega SIEMPRE la lista
   * acumulada). Sube los que aún no se conocen y descarta los que ya no están,
   * comparando por identidad del `File` y no por nombre.
   */
  const syncFiles = useCallback(
    (incoming: File[]) => {
      const current = documentsRef.current;
      const added = incoming.filter(
        (file) => !current.some((doc) => doc.file === file),
      );
      const removed = current.filter((doc) => !incoming.includes(doc.file));

      if (removed.length > 0) {
        // Que el archivo salga de la lista externa ES quitarlo: hay que abortar
        // lo que siga subiendo y borrar el registro, igual que `removeDocument`.
        // Sin esto cada quitar dejaba un upload huérfano en el backend.
        for (const doc of removed) discardUpload(doc);
        const removedKeys = new Set(removed.map((doc) => doc.key));
        setDocuments((prev) => prev.filter((doc) => !removedKeys.has(doc.key)));
      }
      addFiles(added);
    },
    [addFiles, discardUpload],
  );

  const removeDocument = useCallback(
    (key: string) => {
      const doc = documentsRef.current.find((d) => d.key === key);
      if (doc) discardUpload(doc);
      setDocuments((prev) => prev.filter((d) => d.key !== key));
    },
    [discardUpload],
  );

  /**
   * Reintenta un archivo que quedó en `error`, típicamente por un corte de red.
   *
   * Si el intento fallido ya tenía una sesión resumible (>100 MB), la retoma
   * en vez de descartarla — `resumeDirectUpload` sólo manda los bytes que a
   * GCS le faltan. Para lo demás (`signed_url`, o una sesión que nunca
   * llegó a conocerse antes de fallar) el registro anterior se descarta y se
   * arranca de cero, como siempre: si no, cada reintento dejaría un `PENDING`
   * huérfano esperando a expirar.
   */
  const retryDocument = useCallback(
    (key: string) => {
      const doc = documentsRef.current.find((d) => d.key === key);
      if (doc?.status !== "error") return;

      if (doc.uploadId && doc.resumableSessionUri) {
        resumeUploadAttempt(
          key,
          doc.file,
          doc.uploadId,
          doc.resumableSessionUri,
        );
        return;
      }

      if (doc.uploadId) {
        cancelUpload(doc.uploadId).catch(() => {
          /* best-effort: si ya expiró, da igual */
        });
      }

      patch(key, {
        status: "uploading",
        progress: 0,
        uploadId: undefined,
        resumableSessionUri: undefined,
        error: undefined,
      });
      startUpload(key, doc.file);
    },
    [patch, startUpload, resumeUploadAttempt],
  );

  /**
   * Limpia la lista SIN tocar el backend. Es lo que corresponde después de
   * enviar: los uploads acaban de quedar asociados a la entidad y borrarlos
   * sería justo lo contrario de lo que se quiere.
   */
  const reset = useCallback(() => {
    setDocuments([]);
    if (persistKey) writePersisted(persistKey, []);
  }, [persistKey]);

  /**
   * Descarta los archivos: aborta lo que siga subiendo y borra los registros del
   * backend. Es lo que corresponde al cerrar o cancelar el modal, donde los
   * archivos nunca llegaron a asociarse a nada.
   */
  const discardAll = useCallback(() => {
    for (const doc of documentsRef.current) discardUpload(doc);
    setDocuments([]);
    if (persistKey) writePersisted(persistKey, []);
  }, [discardUpload, persistKey]);

  /**
   * Al montar, revalida contra el backend lo que quedó guardado.
   *
   * Sólo se puede recuperar lo que ya está `COMPLETED`: tras una recarga el
   * `File` original desapareció, así que un upload en `PENDING` no se puede
   * reanudar —no quedan bytes que enviar— y se descarta para que el usuario
   * vuelva a adjuntarlo.
   */
  useEffect(() => {
    if (!persistKey) return;

    const stored = readPersisted(persistKey);
    if (stored.length === 0) {
      setIsRecovering(false);
      return;
    }

    let cancelled = false;

    const recover = async () => {
      const checked = await Promise.all(
        stored.map(async (entry) => {
          try {
            const { status } = await getUploadStatus(entry.uploadId);
            return status === "COMPLETED" ? entry : null;
          } catch {
            return null;
          }
        }),
      );
      if (cancelled) return;

      const alive = checked.filter((e): e is PersistedUpload => e !== null);
      const recovered: DocumentUpload[] = alive.map((entry) => ({
        key: `doc-${nextKey.current++}`,
        file: describeRecoveredFile(entry),
        status: "completed" as const,
        progress: 100,
        uploadId: entry.uploadId,
      }));
      // Se antepone en vez de reemplazar: revalidar tarda lo que tarde la red y
      // nada impide al usuario adjuntar mientras tanto. Reemplazar aquí borraba
      // esos archivos —y abandonaba sus registros— sin que nadie lo pidiera.
      setDocuments((prev) => [...recovered, ...prev]);
      writePersisted(persistKey, alive);
      setIsRecovering(false);
    };

    void recover();
    return () => {
      cancelled = true;
    };
  }, [persistKey]);

  // Guarda sólo lo confirmado: un upload a medias no sirve tras la recarga.
  useEffect(() => {
    if (!persistKey || isRecovering) return;
    writePersisted(
      persistKey,
      documents
        .filter((doc) => doc.status === "completed" && doc.uploadId)
        .map((doc) => ({
          uploadId: doc.uploadId as string,
          name: doc.file.name,
          size: doc.file.size,
          type: doc.file.type,
        })),
    );
  }, [documents, persistKey, isRecovering]);

  const isUploading = documents.some((doc) => doc.status === "uploading");
  const hasErrors = documents.some((doc) => doc.status === "error");
  const files = documents.map((doc) => doc.file);
  const uploadIds = documents
    .filter((doc) => doc.status === "completed" && doc.uploadId)
    .map((doc) => doc.uploadId as string);

  return {
    documents,
    files,
    uploadIds,
    isUploading,
    /** Verdadero mientras se revalidan los uploads guardados tras una recarga. */
    isRecovering,
    hasErrors,
    addFiles,
    syncFiles,
    retryDocument,
    removeDocument,
    reset,
    discardAll,
  };
};
